/**
 * PATH: packages/ui/src/kanban/components/kanbanHoverIntent.ts
 * FILE_ID: UI-KANBAN-HOVER-INTENT-001
 * PURPOSE: Shared dwell + scroll-suppress signals for card hover chrome.
 * WHY: Skimming / scrolling a dense lane must not expand action zones; chrome
 *   arms only after the pointer rests ~200ms AND the board is not scrolling.
 *   Hotkeys stay on the separate data-card-id path and ignore this module.
 * FLOW: LaneBody/board scroll → markKanbanScrollActive → quiet timer →
 *   subscribers (useKanbanCardInteractions) cancel / restart chrome dwell.
 */
import { useEffect, useState } from 'react'

/** Pointer must rest this long on a card before hover chrome (lift / action
 *  zone / strip / note) appears. Distinct from checklist hoverExpand (350ms).
 *
 *  PERF-2026-07-28-KANBAN-HOVER-DWELL: was 400ms. 400ms is above the ~250ms
 *  threshold where a UI stops reading as a direct response to the pointer and
 *  starts reading as lag, so deliberate hovers felt unresponsive even though
 *  nothing was actually slow. The dwell exists to stop chrome arming while the
 *  pointer is TRAVELLING across a dense lane, and the scroll-quiet gate below
 *  already covers the scrolling case independently.
 *
 *  This must stay strictly above SCROLL_QUIET_MS: a dwell shorter than the
 *  post-scroll quiet window could arm chrome inside the window the quiet timer
 *  is meant to suppress. 200 vs 180 keeps that ordering with the smallest
 *  value that still reads as intentional rest. */
export const CHROME_DWELL_MS = 200

/** After the last lane/board scroll event, keep chrome suppressed this long
 *  so slow scrolls don't arm mid-pass. */
export const SCROLL_QUIET_MS = 180

type Listener = (active: boolean) => void

let scrollActive = false
let quietTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<Listener>()

function publish(next: boolean) {
  if (scrollActive === next) return
  scrollActive = next
  for (const listener of listeners) listener(scrollActive)
}

/** f() -> whether a kanban lane/board scroll is currently suppressing chrome. */
export function isKanbanScrollActive(): boolean {
  return scrollActive
}

/** f() -> mark scroll-active and reset the quiet timer. Call from lane/board
 *  scroll handlers (not from peek/popover inner scrolls). */
export function markKanbanScrollActive(): void {
  publish(true)
  if (quietTimer) clearTimeout(quietTimer)
  quietTimer = setTimeout(() => {
    quietTimer = null
    publish(false)
  }, SCROLL_QUIET_MS)
}

/** f(listener) -> unsubscribe. Notifies immediately with the current value. */
export function subscribeKanbanScrollActive(listener: Listener): () => void {
  listeners.add(listener)
  listener(scrollActive)
  return () => {
    listeners.delete(listener)
  }
}

/** React hook: live scroll-active flag for card hover chrome gating. */
export function useKanbanScrollActive(): boolean {
  const [active, setActive] = useState(isKanbanScrollActive)
  useEffect(() => subscribeKanbanScrollActive(setActive), [])
  return active
}

/** Test-only: clear timers + force inactive. */
export function __resetKanbanHoverIntentForTests(): void {
  if (quietTimer) {
    clearTimeout(quietTimer)
    quietTimer = null
  }
  publish(false)
}
